import { useEffect, useState } from "react";
import {
  useAssignedClientSelection,
  useFetch,
  useRequest,
} from "../../../hooks";
import { WellProps } from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";

interface WellsFetchResponse {
  success?: boolean;
  data: WellProps[];
}

export default function ChemicalTrackerClient() {
  const { handleRequest } = useRequest();
  const {
    clientOptions,
    selectedClientId,
    setSelectedClientId,
    showClientFilter,
  } = useAssignedClientSelection();

  const { data: wellsResp, isLoading: wellsLoading } =
    useFetch<WellsFetchResponse>(
      selectedClientId ? `well/client/${selectedClientId}` : undefined,
    );
  const wells: WellProps[] = wellsResp?.data ?? [];

  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  const getWellName = (idWell?: number) =>
    wells.find((w) => w.id === idWell)?.name ?? "";

  useEffect(() => {
    setSelectedWellId(null);
    setRows([]);
  }, [selectedClientId]);

  const handleWellSelect = (wellId: number | null) => {
    setSelectedWellId(wellId);
    setRows([]);
    if (wellId == null) return;
    setRowsLoading(true);
    handleRequest({ endpoint: `chemical-list/${wellId}`, options: { method: "GET" } })
      .then((res) => {
        const items: any[] = Array.isArray(res.data) ? res.data : res.data?.rows ?? [];
        const mapped: DocRow[] = items.map((item: any) => ({
          rowId: `c-${item.id}`,
          customName: item.customName,
          date: item.date ?? "",
          wellId: item.idWell ?? wellId,
          wellName: getWellName(item.idWell ?? wellId),
          source: "uploaded" as const,
          path: item.path,
          owner: item.user?.name ?? "—",
        }));
        setRows(mapped.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  };

  const downloadFile = (path: string, name: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const opts: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key: path }),
        headers: { responseType: "Buffer" } as any,
      };
      handleRequest({ endpoint: "pdf-chemical-adm", options: opts })
        .then((res) => {
          if (!res.success) { reject(new Error("Failed")); return; }
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve();
        })
        .catch(reject);
    });

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.path) await downloadFile(row.path, row.customName);
  };

  return (
    <DocumentsPortalLayout
      title="Chemical Trackers"
      subtitle="Browse, filter and download client chemical trackers"
      wells={wells}
      wellsLoading={wellsLoading}
      rows={rows}
      rowsLoading={rowsLoading}
      selectedWellId={selectedWellId}
      onWellSelect={handleWellSelect}
      onDownload={handleDownload}
      showSource={false}
      showClientFilter={showClientFilter}
      selectedClientId={selectedClientId}
      clientOptions={clientOptions}
      onClientChange={(client) => setSelectedClientId(client?.id ?? null)}
      noWellMessage="Select a well to view chemical trackers"
      isError={isError}
    />
  );
}
